import type { TeamStatSummary } from '../types'

/**
 * A quarter-by-quarter breakdown that has been checked against the final score.
 *
 * @remarks
 * `unavailable` is deliberately a distinct case rather than an array of zeros.
 * A save state can contain a quarter buffer that disagrees with the final
 * score, and rendering it anyway states something false about the game.
 *
 * Overtime has two shapes. Vanilla Tecmo reuses the four quarter slots for the
 * extra period, so `home` / `away` are `null` and only the OT column is known.
 * A buffer that still holds regulation scoring (or a ROM hack that keeps it)
 * keeps those four numbers and puts the remainder in OT.
 *
 * Double overtime is a second save merged into this box score. 2OT points sit
 * on `secondOvertimeScore`; they are peeled off the final before the single-OT
 * arithmetic runs, then shown as their own column.
 */
export type QuarterBreakdown =
  | { kind: 'unavailable' }
  | { kind: 'regulation'; home: number[]; away: number[] }
  | {
      kind: 'overtime'
      /** Q1–Q4, or `null` when Tecmo overwrote them with overtime scoring. */
      home: number[] | null
      away: number[] | null
      homeOvertime: number
      awayOvertime: number
      /** Each side's score when overtime began, if that can be inferred. */
      regulationTiedAt: number
      /** Present when this game was merged from a second save. */
      homeSecondOvertime?: number
      awaySecondOvertime?: number
    }

const REGULATION_QUARTERS = 4

function regulationQuarters(team: TeamStatSummary): number[] | null {
  const quarters = team.quarterScores
  if (!Array.isArray(quarters) || quarters.length < REGULATION_QUARTERS) {
    return null
  }

  const slice = quarters.slice(0, REGULATION_QUARTERS)
  return slice.every(value => Number.isFinite(value) && value >= 0) ? slice : null
}

function sum(values: number[]): number {
  return values.reduce((running, value) => running + value, 0)
}

/**
 * Decides whether a game's quarter scores can be shown as fact.
 *
 * @param home - Home side of `games.metadata`, straight from the parser.
 * @param away - Away side of `games.metadata`.
 * @returns `regulation` or `overtime` when the breakdown reconciles with both
 * final scores, otherwise `unavailable`.
 *
 * @remarks
 * Tecmo writes per-quarter scoring to a small RAM buffer. Save states taken on
 * the post-game screen can carry a buffer that is zeroed, stale, or — in
 * overtime — reused for the extra period. Vanilla TSB starts overtime by
 * resetting the quarter index, so Q1 holds the OT score and Q2–Q4 are zero;
 * the ROM hack lists even call this out as "keep quarter scores intact if the
 * game goes into overtime." The only defence after the fact is arithmetic.
 *
 * Three reconciliations, in order:
 *
 * 1. The four slots sum to both finals: regulation, including a genuine 0–0.
 * 2. Both sides are tied after four, and the remainder is the OT column: the
 *    buffer survived into overtime.
 * 3. Subtracting the buffer from both finals leaves a tie, and the buffer
 *    itself has points: vanilla overtime wipe. The four slots are OT scoring,
 *    not Q1–Q4, so those cells stay blank.
 *
 * A leftover that does not leave a regulation tie is refused rather than shown
 * as a fake OT column.
 *
 * @example
 * ```typescript
 * // Intact buffer: 21-21 after four, 27-21 final.
 * resolveQuarterBreakdown(home, away)
 * // => { kind: 'overtime', homeOvertime: 6, awayOvertime: 0, home: [...], ... }
 *
 * // Vanilla TSB wipe: PHX [0,0,0,0] final 14, NO [6,0,0,0] final 20.
 * resolveQuarterBreakdown(home, away)
 * // => { kind: 'overtime', home: null, homeOvertime: 0, awayOvertime: 6, regulationTiedAt: 14 }
 * ```
 */
export function resolveQuarterBreakdown(
  home: TeamStatSummary,
  away: TeamStatSummary
): QuarterBreakdown {
  const doubleOvertime =
    home.secondOvertimeScore != null && away.secondOvertimeScore != null
  const homeSecondOvertime = home.secondOvertimeScore ?? 0
  const awaySecondOvertime = away.secondOvertimeScore ?? 0

  const throughFirstOt: QuarterBreakdown = resolveThroughFirstOvertime(
    doubleOvertime ? { ...home, score: home.score - homeSecondOvertime } : home,
    doubleOvertime ? { ...away, score: away.score - awaySecondOvertime } : away
  )

  if (!doubleOvertime) return throughFirstOt

  if (throughFirstOt.kind === 'unavailable') return throughFirstOt

  if (throughFirstOt.kind === 'regulation') {
    return {
      kind: 'overtime',
      home: throughFirstOt.home,
      away: throughFirstOt.away,
      homeOvertime: 0,
      awayOvertime: 0,
      regulationTiedAt: sum(throughFirstOt.home),
      homeSecondOvertime,
      awaySecondOvertime,
    }
  }

  return { ...throughFirstOt, homeSecondOvertime, awaySecondOvertime }
}

function resolveThroughFirstOvertime(
  home: TeamStatSummary,
  away: TeamStatSummary
): QuarterBreakdown {
  const homeQuarters = regulationQuarters(home)
  const awayQuarters = regulationQuarters(away)
  if (!homeQuarters || !awayQuarters) {
    return { kind: 'unavailable' }
  }

  const homeBuffer = sum(homeQuarters)
  const awayBuffer = sum(awayQuarters)

  if (homeBuffer === home.score && awayBuffer === away.score) {
    return { kind: 'regulation', home: homeQuarters, away: awayQuarters }
  }

  const homeRemainder = home.score - homeBuffer
  const awayRemainder = away.score - awayBuffer
  const scoredInOvertime = homeRemainder > 0 || awayRemainder > 0

  // Buffer still has Q1–Q4, leftover is overtime.
  if (homeRemainder >= 0 && awayRemainder >= 0 && homeBuffer === awayBuffer && scoredInOvertime) {
    return {
      kind: 'overtime',
      home: homeQuarters,
      away: awayQuarters,
      homeOvertime: homeRemainder,
      awayOvertime: awayRemainder,
      regulationTiedAt: homeBuffer,
    }
  }

  // Vanilla TSB overtime: the four slots were reset and now hold only OT.
  const homeImpliedRegulation = home.score - homeBuffer
  const awayImpliedRegulation = away.score - awayBuffer
  if (
    homeImpliedRegulation === awayImpliedRegulation &&
    homeImpliedRegulation >= 0 &&
    (homeBuffer > 0 || awayBuffer > 0)
  ) {
    return {
      kind: 'overtime',
      home: null,
      away: null,
      homeOvertime: homeBuffer,
      awayOvertime: awayBuffer,
      regulationTiedAt: homeImpliedRegulation,
    }
  }

  return { kind: 'unavailable' }
}
